import type { NtCourse, NtPlacement, NtProposal, NtSchedule, NtStudent, StudentDoc } from '../../core/types'
import { gradeFor } from '../../core/students'

/**
 * The prep dashboard's arithmetic — pure, so it is tested
 * (tests/dashboard.test.ts) rather than trusted.
 *
 * Two questions, both an admin's in the weeks before the term: which teachers
 * have not done their part, and where are students falling through — by grade
 * and by seminar, because those are the two levers an admin actually has
 * (a grade-level announcement, a word with a seminar teacher).
 */

/* ---------------- teachers ---------------- */

export type CourseStage = 'none' | 'draft' | 'submitted' | 'returned' | 'approved'

export interface TeacherRow {
  email: string
  name: string
  course: NtCourse | null
  stage: CourseStage
  unplanned: number
  /** The teacher's four prep items: artifact options, proposed, planned, approved. */
  done: number
  artifacts: boolean
}

/**
 * One row per teacher: everyone holding the Teacher role, plus anyone with a
 * course who does not (a district admin teaching one). A course is matched by
 * the teacher's email, or by name for courses made before email was stored.
 * Not-done first, then by name — the list is a to-chase list.
 */
export function teacherProgress(
  teachers: Array<{ email: string; name: string }>,
  courses: NtCourse[],
  unplanned: (c: NtCourse) => number,
): TeacherRow[] {
  const people = new Map(teachers.map((t) => [t.email.toLowerCase(), t.name]))
  for (const c of courses) {
    const key = c.teacherEmail?.toLowerCase()
      ?? [...people.entries()].find(([, n]) => n === c.teacherName)?.[0]
      ?? `name:${c.teacherName}`
    if (!people.has(key)) people.set(key, c.teacherName)
  }
  const rows = [...people.entries()].map(([email, name]): TeacherRow => {
    const theirs = courses.filter((c) => (c.teacherEmail?.toLowerCase() ?? '') === email
      || (!c.teacherEmail && c.teacherName === name))
    // Their most advanced course is the one that tells you where they are.
    const rank: Record<NtCourse['status'], number> = { draft: 1, returned: 2, submitted: 3, approved: 4 }
    const course = theirs.sort((a, b) => rank[b.status] - rank[a.status])[0] ?? null
    const stage: CourseStage = course ? course.status : 'none'
    const gaps = course ? unplanned(course) : 0
    const artifacts = !!course && (course.artifactOptions ?? []).some((o) => o.description.trim())
    const done = (artifacts ? 1 : 0) + (stage !== 'none' && stage !== 'draft' ? 1 : 0) + (course && gaps === 0 ? 1 : 0) + (stage === 'approved' ? 1 : 0)
    return { email: email.startsWith('name:') ? '' : email, name, course, stage, unplanned: gaps, done, artifacts }
  })
  return rows.sort((a, b) => a.done - b.done || a.name.localeCompare(b.name))
}

/* ---------------- students ---------------- */

export type Placed = 'both' | 'partial' | 'none' | 'independent' | 'not run'
export type ProjectStage = 'none' | NtProposal['status']

export interface StudentRow {
  email: string
  name: string
  grade: number
  seminar: string
  ranked: boolean
  /** How many courses they have picked so far. */
  picked: number
  project: ProjectStage
  placed: Placed
  /** Where they make their open-house artifact — or why that does not apply yet. */
  artifact: 'AM' | 'PM' | 'not chosen' | 'independent' | 'not placed' | 'none'
}

export function studentProgress(
  roster: StudentDoc[],
  rankings: NtStudent[],
  proposals: NtProposal[],
  placement: NtPlacement | null,
  ranksRequired: number,
  now: Date = new Date(),
  schedules: NtSchedule[] = [],
): StudentRow[] {
  const sched = new Map(schedules.map((x) => [x.studentId, x]))
  const ranks = new Map(rankings.map((s) => [s.studentId, s.ranks.length]))
  const props = new Map(proposals.map((p) => [p.studentEmail, p.status]))
  const rows = new Map((placement?.rows ?? []).map((r) => [r.studentId, r]))
  return roster.filter((s) => s.active).map((s) => {
    const r = rows.get(s.studentId)
    const placed: Placed = !placement ? 'not run'
      : !r ? 'none'
      : r.independent ? 'independent'
      : r.amCourseId && r.pmCourseId ? 'both'
      : r.amCourseId || r.pmCourseId ? 'partial' : 'none'
    return {
      email: s.email, name: s.displayName, grade: gradeFor(s.classYear, now),
      seminar: s.seminarTeacherName || s.seminarTeacherEmail,
      ranked: (ranks.get(s.studentId) ?? 0) >= ranksRequired,
      picked: ranks.get(s.studentId) ?? 0,
      project: props.get(s.email) ?? 'none',
      placed,
      artifact: ((): StudentRow['artifact'] => {
        const x = sched.get(s.studentId)
        if (!x) return 'not placed'
        if (x.independent) return 'independent'
        if (x.noArtifact) return 'none'
        return x.artifactBlock ?? 'not chosen'
      })(),
    }
  })
}

export interface GroupRow {
  key: string
  students: number
  ranked: number
  /** Submitted, being checked, or sent back — somewhere in the pipeline. */
  projectsInProgress: number
  projectsApproved: number
  /** In two courses or on an approved project. */
  placed: number
  /** Artifact course chosen, or on an independent project (which is their artifact). */
  artifactChosen: number
}

/** Roll students up by grade or by seminar, worst-ranked first. */
export function groupStudents(rows: StudentRow[], by: 'grade' | 'seminar'): GroupRow[] {
  const groups = new Map<string, StudentRow[]>()
  for (const r of rows) {
    const k = by === 'grade' ? `Grade ${r.grade}` : r.seminar || 'No seminar'
    groups.set(k, [...(groups.get(k) ?? []), r])
  }
  return [...groups.entries()].map(([key, rs]) => ({
    key,
    students: rs.length,
    ranked: rs.filter((r) => r.ranked).length,
    projectsInProgress: rs.filter((r) => ['submitted', 'checked', 'returned'].includes(r.project)).length,
    projectsApproved: rs.filter((r) => r.project === 'approved').length,
    placed: rs.filter((r) => r.placed === 'both' || r.placed === 'independent').length,
    artifactChosen: rs.filter((r) => r.artifact === 'AM' || r.artifact === 'PM' || r.artifact === 'independent').length,
  })).sort((a, b) => (a.ranked / a.students) - (b.ranked / b.students)
    // "Grade 9" before "Grade 10": compare the numbers, not the text.
    || a.key.localeCompare(b.key, undefined, { numeric: true }))
}

/**
 * What a student still has to finish, in words a teacher can say to them:
 * the list a seminar teacher or admin uses to call students in. Empty means
 * nothing is waiting on the student.
 */
export function followUps(r: StudentRow, ranksRequired: number): string[] {
  const out: string[] = []
  if (!r.ranked) out.push(r.picked ? `Course selections: ${r.picked} of ${ranksRequired} picked, not submitted` : 'Course selections not started')
  if (r.project === 'returned') out.push('Project proposal sent back: needs revising')
  if (r.project === 'draft') out.push('Project proposal still a draft: not submitted')
  if (r.artifact === 'not chosen') out.push('Has not chosen an artifact course')
  return out
}
